import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Bell, LayoutGrid, Target, UserRound } from "lucide-react";
import { PageBreadcrumb } from "../PageBreadcrumb";

/** Pages de l'espace connecté qui portent la sous-navigation (cadrage, 4.1). */
export function estPageEspace(pathname: string): boolean {
  return /^\/(home|mon-keno|compte)(\/|$)/.test(pathname);
}

/** Lien actif : l'onglet Alertes de Mes grilles se distingue de Mes grilles par ?tab=alertes. */
function actif(pathname: string, search: string): "home" | "mon-keno" | "alertes" | "compte" | null {
  if (pathname.startsWith("/home")) return "home";
  if (pathname.startsWith("/compte")) return "compte";
  if (pathname.startsWith("/mon-keno")) return new URLSearchParams(search).get("tab") === "alertes" ? "alertes" : "mon-keno";
  return null;
}

/** Retrait du lien actif ramené dans la vue, au-delà du fondu des bords (mag-header.css). */
const MARGE = 16;

/** Sous-navigation « Mon espace » des trois pages de l'espace (D1, maquette du 16/10/2026). */
export const EspaceSubnav = () => {
  const { pathname, search } = useLocation();
  const courant = actif(pathname, search);
  const ref = useRef<HTMLElement>(null);
  // Rangée défilante sur mobile : le lien actif n'est ramené dans la vue que s'il en sort, au plus près
  // et à 16 px du bord (hors des fondus), sans faire défiler la page. Centré, il coupait les deux voisins.
  useEffect(() => {
    const nav = ref.current;
    const lienActif = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !lienActif || nav.scrollWidth <= nav.clientWidth) return;
    const n = nav.getBoundingClientRect();
    const a = lienActif.getBoundingClientRect();
    if (a.left < n.left + MARGE) nav.scrollLeft -= n.left + MARGE - a.left;
    else if (a.right > n.right - MARGE) nav.scrollLeft += a.right - (n.right - MARGE);
  }, [courant]);
  const lien = (cle: typeof courant, to: string, icone: React.ReactNode, texte: string) => (
    <Link to={to} aria-current={courant === cle ? "page" : undefined}>{icone}{texte}</Link>
  );

  // Fil d'Ariane des pages de Mon espace (décision du 26/10/2026) : Accueil > Mon espace > page.
  const titres = { home: "Tableau de bord", "mon-keno": "Mes grilles", alertes: "Alertes", compte: "Mon compte" } as const;
  return (
    <>
    {courant && <PageBreadcrumb current={titres[courant]} currentPath={pathname} items={courant === "home" ? [] : [{ label: "Mon espace", href: "/home/" }]} />}
    <nav ref={ref} className="km-subnav" aria-label="Mon espace" data-nav-emplacement="sous_navigation_espace">
      <span className="km-subnav__lbl">Mon espace</span>
      {lien("home", "/home/", <LayoutGrid aria-hidden="true" />, "Tableau de bord")}
      {lien("mon-keno", "/mon-keno/", <Target aria-hidden="true" />, "Mes grilles")}
      {lien("alertes", "/mon-keno/?tab=alertes", <Bell aria-hidden="true" />, "Alertes")}
      {lien("compte", "/compte/", <UserRound aria-hidden="true" />, "Compte")}
    </nav>
    </>
  );
};
